// Registry of Dr. Mavroson's concierge programs. One generic form
// (components/ConciergeProgramForm.jsx) renders any of them; each entry
// supplies copy, billing, images, the question flow and the flow's rules.
// Backend contract: docs/BACKEND_CONCIERGE_PROGRAMS_PRD.md.
import {
  ADVANCED_PEPTIDE_PRICE, ADVANCED_PEPTIDE_PLAN_SLUG, ADVANCED_PEPTIDE_NAME, ADVANCED_PEPTIDE_PRIMARY_GOAL,
  MEMBERSHIP_DESCRIPTION, MEMBERSHIP_INCLUDES, MEMBERSHIP_FOOTNOTE, PROGRAM_IMAGE,
  buildDynamicAdvancedPeptideFlow, isDisqualifyingAnswer as peptideDisqualify, flaggedConditions,
} from './advancedPeptideData';
import {
  ADVANCED_WEIGHT_LOSS_PRICE, ADVANCED_WEIGHT_LOSS_PLAN_SLUG, ADVANCED_WEIGHT_LOSS_NAME, ADVANCED_WEIGHT_LOSS_PRIMARY_GOAL,
  WEIGHT_LOSS_DESCRIPTION, WEIGHT_LOSS_INCLUDES, WEIGHT_LOSS_FOOTNOTE, WEIGHT_LOSS_IMAGE,
  buildDynamicAdvancedWeightLossFlow, isDisqualifyingAnswer as weightLossDisqualify, weightLossFlags,
} from './advancedWeightLossData';
import {
  ADVANCED_LONGEVITY_INITIAL_PRICE, ADVANCED_LONGEVITY_MONTHLY_PRICE, ADVANCED_LONGEVITY_PLAN_SLUG, ADVANCED_LONGEVITY_NAME, ADVANCED_LONGEVITY_PRIMARY_GOAL,
  LONGEVITY_DESCRIPTION, LONGEVITY_INCLUDES, LONGEVITY_FOOTNOTE, LONGEVITY_IMAGE,
  buildDynamicAdvancedLongevityFlow, isDisqualifyingAnswer as longevityDisqualify, longevityFlags,
} from './advancedLongevityData';

// billing: { todayCents, monthlyCents, priceLabel, summary, todayLine }
//   todayCents   — what Square charges at checkout (the FE amount is display-only;
//                  the BE prices from planId).
//   monthlyCents — the recurring amount from the second cycle.
const monthly = (dollars) => ({
  todayCents: dollars * 100, monthlyCents: dollars * 100, priceLabel: `$${dollars}/mo`,
  summary: 'Billed today, then every month on this date.', todayLine: `$${dollars}.00`,
});
const introThenMonthly = (first, then) => ({
  todayCents: first * 100, monthlyCents: then * 100, priceLabel: `$${first} first month, then $${then}/mo`,
  summary: `$${first} today for your first month, then $${then} every month from month 2.`, todayLine: `$${first}.00`,
});

export const CONCIERGE_PROGRAMS = {
  advancedPeptide: {
    key: 'advancedPeptide', route: '/advanced-peptide', storageKey: 'fitpal_advanced_peptide_progress',
    name: ADVANCED_PEPTIDE_NAME, planSlug: ADVANCED_PEPTIDE_PLAN_SLUG, primaryGoal: ADVANCED_PEPTIDE_PRIMARY_GOAL,
    price: ADVANCED_PEPTIDE_PRICE, billing: monthly(ADVANCED_PEPTIDE_PRICE),
    description: MEMBERSHIP_DESCRIPTION, includes: MEMBERSHIP_INCLUDES, footnote: MEMBERSHIP_FOOTNOTE, image: PROGRAM_IMAGE,
    overviewSubtitle: 'Monthly membership · 100% online · Expert guidance & support',
    recommendHeadline: "You're eligible for a personalized peptide plan.",
    buildFlow: buildDynamicAdvancedPeptideFlow, isDisqualifyingAnswer: peptideDisqualify, flags: flaggedConditions,
  },
  advancedWeightLoss: {
    key: 'advancedWeightLoss', route: '/advanced-weight-loss', storageKey: 'fitpal_advanced_weight_loss_progress',
    name: ADVANCED_WEIGHT_LOSS_NAME, planSlug: ADVANCED_WEIGHT_LOSS_PLAN_SLUG, primaryGoal: ADVANCED_WEIGHT_LOSS_PRIMARY_GOAL,
    price: ADVANCED_WEIGHT_LOSS_PRICE, billing: monthly(ADVANCED_WEIGHT_LOSS_PRICE),
    description: WEIGHT_LOSS_DESCRIPTION, includes: WEIGHT_LOSS_INCLUDES, footnote: WEIGHT_LOSS_FOOTNOTE, image: WEIGHT_LOSS_IMAGE,
    overviewSubtitle: 'Monthly membership · 100% online · Physician-guided weight management',
    recommendHeadline: "You're eligible for physician-guided weight-loss support.",
    buildFlow: buildDynamicAdvancedWeightLossFlow, isDisqualifyingAnswer: weightLossDisqualify, flags: weightLossFlags,
  },
  advancedLongevity: {
    key: 'advancedLongevity', route: '/advanced-longevity', storageKey: 'fitpal_advanced_longevity_progress',
    name: ADVANCED_LONGEVITY_NAME, planSlug: ADVANCED_LONGEVITY_PLAN_SLUG, primaryGoal: ADVANCED_LONGEVITY_PRIMARY_GOAL,
    price: ADVANCED_LONGEVITY_INITIAL_PRICE, billing: introThenMonthly(ADVANCED_LONGEVITY_INITIAL_PRICE, ADVANCED_LONGEVITY_MONTHLY_PRICE),
    description: LONGEVITY_DESCRIPTION, includes: LONGEVITY_INCLUDES, footnote: LONGEVITY_FOOTNOTE, image: LONGEVITY_IMAGE,
    overviewSubtitle: 'Monthly membership · 100% online · Physician-guided healthy aging',
    recommendHeadline: "You're eligible for a personalized longevity plan.",
    buildFlow: buildDynamicAdvancedLongevityFlow, isDisqualifyingAnswer: longevityDisqualify, flags: longevityFlags,
  },
};

export const CONCIERGE_PROGRAM_KEYS = Object.keys(CONCIERGE_PROGRAMS);
